import { google, Loader } from 'google-maps'

/* Utils */
import { joinPartsOfAddress } from '@/utils/joinPartsOfAddress'

/* Types */
import {
  AutocompleteService,
  AutocompleteSuggestion,
} from '@/types/mapServices'
import { StateOfAutocomplete } from '@/constants/components/autocomplete'

let api = null as null | Promise<google>

export function getGoogleApi(apiKey: string): Promise<google> {
  if (!api) {
    const loader = new Loader(apiKey, {
      libraries: ['places', 'geometry'],
      language: 'ru',
    })
    api = loader.load()
  }

  return api
}

export function makeGoogleAddressString(
  suggestion: google.maps.places.AutocompletePrediction,
): string | null {
  const street = suggestion.structured_formatting.main_text.split(', ')[0]
  const house = suggestion.structured_formatting.main_text.split(', ')[1]
  const locality =
    suggestion.structured_formatting.secondary_text.split(', ')[0]

  const address = joinPartsOfAddress([locality, street, house], ', ')

  if (!address) return null

  return address
}

export function makeShortGoogleAddressString(
  suggestion: google.maps.places.AutocompletePrediction,
): string | undefined {
  const street = suggestion.structured_formatting.main_text.split(', ')[0]
  const house = suggestion.structured_formatting.main_text.split(', ')[1]
  const locality =
    suggestion.structured_formatting.secondary_text.split(', ')[0]

  const address = joinPartsOfAddress([street, house])

  if (!address) return locality

  if (!address) return undefined

  return address
}
export function transformGoogleAutocomplete(
  googleSuggestions: google.maps.places.AutocompletePrediction[],
): AutocompleteSuggestion[] {
  const autocompleteList: AutocompleteSuggestion[] = []

  googleSuggestions.forEach((suggestion) => {
    const address = makeGoogleAddressString(suggestion)
    const shortAddress = makeShortGoogleAddressString(suggestion)

    if (address && shortAddress)
      autocompleteList.push({
        value: address || shortAddress,
      })
  })

  if (autocompleteList.length === 0) throw new Error(StateOfAutocomplete.EMPTY)

  return autocompleteList
}

export async function makeGoogleAutocompleteService(
  apiKey: string,
): Promise<AutocompleteService> {
  const googleApi = await getGoogleApi(apiKey)
  const autocompleteService = new googleApi.maps.places.AutocompleteService()

  function getSuggestions(request: string): Promise<AutocompleteSuggestion[]> {
    return new Promise((resolve, reject) => {
      autocompleteService.getPlacePredictions(
        { input: request, types: ['geocode'] },
        (result: any, status: any) => {
          switch (status) {
            case googleApi.maps.places.PlacesServiceStatus.ZERO_RESULTS:
            case googleApi.maps.places.PlacesServiceStatus.OK:
              try {
                resolve(transformGoogleAutocomplete(result))
              } catch (error) {
                reject(new Error(error))
              }
              break
            default:
              reject(new Error(status))
          }
        },
      )
    })
  }

  return {
    getSuggestions,
  }
}
